import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Play, Pause } from 'lucide-react';
import {
  useSchedule,
  useExecutionHistory,
  useDeleteSchedule,
  useExecuteSchedule,
  usePauseSchedule,
  useResumeSchedule,
} from '@/hooks/reporting/useSchedules';
import ExecutionHistoryTable from '@/components/reports/tables/ExecutionHistoryTable';
import ConfirmDeleteModal from '@/components/reports/modals/ConfirmDeleteModal';
import EmptyState from '@/components/reports/common/EmptyState';
import type { ExecutionHistoryQuery } from '@/types/reporting-v2';

export default function ScheduleDetail() {
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const navigate = useNavigate();

  const { data: schedule, isLoading, error, refetch } = useSchedule(scheduleId);
  const deleteSchedule = useDeleteSchedule();
  const executeSchedule = useExecuteSchedule();
  const pauseSchedule = usePauseSchedule();
  const resumeSchedule = useResumeSchedule();

  const [historyQuery, setHistoryQuery] = useState<ExecutionHistoryQuery>({
    page: 1,
    limit: 10,
  });

  const { data: history, isLoading: loadingHistory } = useExecutionHistory(
    scheduleId,
    historyQuery
  );

  const [deleteModal, setDeleteModal] = useState(false);

  const handleExecute = async () => {
    await executeSchedule.mutateAsync({ scheduleId: scheduleId! });
    refetch();
  };

  const handlePauseResume = async () => {
    if (schedule?.is_active) {
      await pauseSchedule.mutateAsync(scheduleId!);
    } else {
      await resumeSchedule.mutateAsync(scheduleId!);
    }
    refetch();
  };

  const handleDelete = async () => {
    await deleteSchedule.mutateAsync(scheduleId!);
    navigate('/admin/reporting/schedules');
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Error loading schedule</h3>
          <p className="text-red-600 text-sm mt-1">{(error as Error).message}</p>
          <button
            onClick={() => navigate('/admin/reporting/schedules')}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
          >
            Back to Schedules
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-4 bg-gray-200 rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className="p-6">
        <EmptyState
          icon={ArrowLeft}
          title="Schedule not found"
          description="The schedule you're looking for doesn't exist or has been deleted."
          action={{
            label: 'Back to Schedules',
            onClick: () => navigate('/admin/reporting/schedules'),
          }}
        />
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatReportType = (type: string) => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/reporting/schedules')}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{schedule.schedule_name}</h1>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  schedule.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {schedule.is_active ? 'Active' : 'Paused'}
              </span>
            </div>
            {schedule.description && (
              <p className="text-sm text-gray-500 mt-1">{schedule.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExecute}
            disabled={executeSchedule.isPending}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 disabled:opacity-50 flex items-center gap-2"
          >
            <Play className="h-4 w-4" />
            Execute Now
          </button>
          <button
            onClick={handlePauseResume}
            disabled={pauseSchedule.isPending || resumeSchedule.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-md hover:bg-gray-100 disabled:opacity-50 flex items-center gap-2"
          >
            {schedule.is_active ? (
              <>
                <Pause className="h-4 w-4" />
                Pause
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                Resume
              </>
            )}
          </button>
          <button
            onClick={() => navigate(`/admin/reporting/schedules/${scheduleId}/edit`)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
          <button
            onClick={() => setDeleteModal(true)}
            className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Schedule Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Schedule Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-gray-500">Report Type</div>
            <div className="text-sm font-medium text-gray-900 mt-1">
              {formatReportType(schedule.report_type)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Frequency</div>
            <div className="text-sm font-medium text-gray-900 mt-1 capitalize">
              {schedule.frequency}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Next Execution</div>
            <div className="text-sm font-medium text-gray-900 mt-1">
              {schedule.next_execution ? formatDate(schedule.next_execution) : 'N/A'}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Last Execution</div>
            <div className="text-sm font-medium text-gray-900 mt-1">
              {schedule.last_execution ? formatDate(schedule.last_execution) : 'Never'}
            </div>
          </div>
        </div>
      </div>

      {/* Execution Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600">Total Executions</div>
          <div className="text-2xl font-bold text-gray-900 mt-2">
            {schedule.execution_stats.total_executions}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600">Successful</div>
          <div className="text-2xl font-bold text-green-600 mt-2">
            {schedule.execution_stats.successful_executions}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600">Failed</div>
          <div className="text-2xl font-bold text-red-600 mt-2">
            {schedule.execution_stats.failed_executions}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600">Success Rate</div>
          <div className="text-2xl font-bold text-blue-600 mt-2">
            {schedule.execution_stats.success_rate}%
          </div>
        </div>
      </div>

      {/* Execution History */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Execution History</h2>
        <ExecutionHistoryTable
          executions={history?.executions || []}
          loading={loadingHistory}
          onViewReport={(snapshotId) => navigate(`/admin/reporting/reports/${snapshotId}`)}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Schedule"
        message="Are you sure you want to delete this schedule? This action cannot be undone."
        itemName={schedule.schedule_name}
        loading={deleteSchedule.isPending}
      />
    </div>
  );
}
